import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { DocsVerificationSideEnum, DocsVerificationTabEnum } from '@models/enums';
import { useProceedVerificationSDKMutation } from '@modules/docs-verification-widget/api';
import type { RootState } from '@store/root-reducer';

export const usePageTitle = () => {
    const { t } = useTranslation();
    const [, { isLoading }] = useProceedVerificationSDKMutation();
    const { currentTab } = useSelector((state: RootState) => state.docsVerification);
    const documentsFiles = useSelector(
        (state: RootState) => state.docsVerification.documents.files,
    ).filter((item) => item.tab === DocsVerificationTabEnum.DOCUMENT);

    const pageTitle = useMemo(() => {
        if (currentTab === DocsVerificationTabEnum.PERSONAL_DATA) {
            return t('docVerificationWidget.start.completePersonalData');
        }
        if (currentTab === DocsVerificationTabEnum.DOCUMENT) {
            const documentFiles = documentsFiles.filter(
                (item) => item.tab === DocsVerificationTabEnum.DOCUMENT,
            );

            if (documentFiles.filter((item) => item.status.loading).length || isLoading) {
                return t('common.pleaseWait');
            }

            const isTwoSideDocument = documentFiles.find(
                (item) => item.isTwoSideDocument && item.status.success,
            );

            if (isTwoSideDocument) {
                if (
                    documentFiles.length === 2 &&
                    documentFiles.filter((item) => item.status.success).length === 2
                ) {
                    return t('docVerificationWidget.documents.checkDocument');
                }

                if (isTwoSideDocument.side === DocsVerificationSideEnum.FRONT) {
                    return t('docVerificationWidget.documents.uploadBackSide');
                }

                return t('docVerificationWidget.documents.uploadFrontSide');
            }

            return t('docVerificationWidget.documents.title');
        }

        if (currentTab === DocsVerificationTabEnum.BIOMETRICS) {
            return t('docVerificationWidget.selfie.title');
        }

        if (currentTab === DocsVerificationTabEnum.ADDRESS) {
            return t('docVerificationWidget.proofAddress.title');
        }

        return '';
    }, [currentTab, documentsFiles, t, isLoading]);

    return { pageTitle };
};
